import React, { useEffect, useState } from 'react';
import { portalApi, sessionHeaders, portalErrorMessage } from './portalApi';
import { askConfirm } from '../AppDialogs';

// The signed-in Customer's Sales Orders in the portal (issue #257): the
// list at /portal/<name>/orders, each order at /portal/<name>/orders/<id>.
// Every login on the Customer sees the same orders from Submitted on; a
// Draft never shows. A portal order Submitted and waiting for staff to
// Accept it (issue #284) can be Edited (back into the cart) or Withdrawn by
// the login that submitted it.
// Status, dates and amounts arrive already worked out and formatted
// (backend/services/portalOrderService.js), since the portal doesn't load
// the back office's Localisation settings.

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };
const INPUT_STYLE = { width: '100%', maxWidth: 320, padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const MUTED = { fontSize: 14, color: '#5a6b85' };
const LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const TH = { padding: '8px 10px 8px 0', fontSize: 12, fontWeight: 600, color: '#5a6b85', textAlign: 'left', whiteSpace: 'nowrap', verticalAlign: 'bottom' };
const TD = { padding: '10px 10px 10px 0', fontSize: 14, verticalAlign: 'top' };
const NUM = { textAlign: 'right' };
const SUB = { fontSize: 11, color: '#888' };
const SEARCH_DELAY_MS = 300;
const PRIMARY_BUTTON = { padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' };
const ACTION_BUTTON = { padding: '7px 12px', border: '1px solid #bfc8d6', color: '#1f2d3d', background: '#fff', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' };

export const STATUS_COLOURS = {
  submitted: { background: '#f3e8fd', color: '#6a1b9a' },
  accepted: { background: '#eef1f5', color: '#3d4b63' },
  being_prepared: { background: '#e8f1fc', color: '#1565c0' },
  part_shipped: { background: '#fff4e0', color: '#8a5a00' },
  shipped: { background: '#e6f4ea', color: '#2e7d32' },
};

export function StatusPill({ status, label }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', ...(STATUS_COLOURS[status] || STATUS_COLOURS.accepted) }}>
      {label}
    </span>
  );
}

// The order's progress in four steps the Customer cares about. Picking and
// packing both read Being prepared; a part-shipped order has the Shipped
// step half done.
const PROGRESS_STEPS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'being_prepared', label: 'Being prepared' },
  { key: 'shipped', label: 'Shipped' },
];
const PROGRESS_REACHED = { submitted: 0, accepted: 1, being_prepared: 2, part_shipped: 3, shipped: 3 };
const STEP_DONE = '#2e7d32';
const STEP_OPEN = '#c9d1dd';

function OrderProgress({ status, label }) {
  const reached = PROGRESS_REACHED[status] ?? 0;
  const partShipped = status === 'part_shipped';
  return (
    <div role="list" aria-label={`Order progress: ${label}`} style={{ display: 'flex', alignItems: 'flex-start', margin: '4px 0 20px', maxWidth: 560 }}>
      {PROGRESS_STEPS.map((step, i) => {
        const done = i <= reached;
        const half = partShipped && i === 3;
        const current = i === reached;
        return (
          <div key={step.key} role="listitem" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {i > 0 && <div style={{ position: 'absolute', top: 8, right: '50%', width: '100%', height: 3, background: done ? STEP_DONE : STEP_OPEN }} />}
            <div
              style={{
                position: 'relative', zIndex: 1, width: 18, height: 18, borderRadius: '50%', boxSizing: 'border-box',
                border: `3px solid ${done ? STEP_DONE : STEP_OPEN}`,
                background: half ? `linear-gradient(90deg, ${STEP_DONE} 50%, #fff 50%)` : (done ? STEP_DONE : '#fff'),
              }}
            />
            <div style={{ marginTop: 6, fontSize: 12, textAlign: 'center', lineHeight: 1.25, color: done ? '#1f2d3d' : '#5a6b85', fontWeight: current ? 700 : 400 }}>
              {half ? 'Part-shipped' : step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function BackorderPill() {
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', background: '#fef2f2', color: '#b91c1c' }}>
      Backorder
    </span>
  );
}

// Loads `path` with the portal session, ending the session on a 401.
export function usePortalGet(portalName, path, params, onSessionEnded, fallback) {
  const [state, setState] = useState({ data: null, error: '', notFound: false });
  const paramsKey = JSON.stringify(params || {});
  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, error: '', notFound: false }));
    portalApi.get(`/api/portal-auth/portals/${encodeURIComponent(portalName)}${path}`, { params: JSON.parse(paramsKey), headers: sessionHeaders(portalName) })
      .then((res) => { if (!cancelled) setState({ data: res.data.data, error: '', notFound: false }); })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 401) onSessionEnded();
        else if (err.response?.status === 404) setState({ data: null, error: '', notFound: true });
        else setState((prev) => ({ ...prev, error: portalErrorMessage(err, fallback) }));
      });
    return () => { cancelled = true; };
  }, [portalName, path, paramsKey, onSessionEnded, fallback]);
  return state;
}

function OrderList({ portalName, ordering, onOpen, onNew, onSessionEnded }) {
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [backordersOnly, setBackordersOnly] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const params = { ...(search ? { search } : {}), ...(backordersOnly ? { backorders: 'true' } : {}) };
  const { data: orders, error } = usePortalGet(portalName, '/orders', params, onSessionEnded, 'Could not load your orders');
  const filtered = Boolean(search) || backordersOnly;

  return (
    <div style={CARD_STYLE}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Orders</h1>
        {ordering.can_order && <button type="button" onClick={onNew} title="Find products and add them to your cart" style={PRIMARY_BUTTON}>New order</button>}
      </div>
      {!ordering.can_order && ordering.message && <div style={{ ...MUTED, marginBottom: 14 }}>{ordering.message}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search order number or PO reference"
          aria-label="Search order number or PO reference"
          style={INPUT_STYLE}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={backordersOnly} onChange={(e) => setBackordersOnly(e.target.checked)} />
          Only orders with backorders
        </label>
      </div>
      {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && orders === null && <div style={MUTED}>Loading...</div>}
      {orders && orders.length === 0 && (
        <div style={MUTED}>{filtered ? 'No orders match.' : 'You have no orders yet.'}</div>
      )}
      {orders && orders.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                <th style={TH}>Order</th>
                <th style={TH}>Date</th>
                <th style={TH}>PO reference</th>
                <th style={TH}>Status</th>
                <th style={{ ...TH, ...NUM }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => onOpen(o.id)}
                  title={`Open ${o.display_number}`}
                  style={{ borderBottom: '1px solid #eef1f5', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f8fc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ ...TD, fontWeight: 500, color: '#1976d2', whiteSpace: 'nowrap' }}>{o.display_number}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{o.order_date}</td>
                  <td style={TD}>{o.po_reference || '—'}</td>
                  <td style={TD}>
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      <StatusPill status={o.status} label={o.status_label} />
                      {o.backordered && <BackorderPill />}
                    </span>
                  </td>
                  <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>
                    {o.total}
                    {o.total_excludes_shipping && <div style={SUB}>excl. shipping</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={LABEL_STYLE}>{label}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{children}</div>
    </div>
  );
}

function TotalRow({ label, value, strong }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 24,
      fontSize: strong ? 17 : 14, fontWeight: strong ? 700 : 400, padding: strong ? '8px 0' : '4px 0',
      ...(strong ? { borderTop: '1px solid #b3bbc7', marginTop: 4 } : {}),
    }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// Edit and Withdraw on a submitted order, for the login that submitted it.
function OrderChanges({ portalName, order, onNavigate, onCartChanged, onSessionEnded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (action, fallback, then) => {
    setBusy(true);
    setError('');
    try {
      await portalApi.post(`/api/portal-auth/portals/${encodeURIComponent(portalName)}/orders/${order.id}/${action}`, null, { headers: sessionHeaders(portalName) });
      onCartChanged();
      then();
      return;
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionEnded();
        return;
      }
      setError(portalErrorMessage(err, fallback));
    }
    setBusy(false);
  };
  const edit = () => run('edit', 'Could not open this order to edit', () => onNavigate('cart'));
  const withdraw = async () => {
    if (!(await askConfirm(`Withdraw order ${order.display_number}? It will be removed and won't be sent to us.`, { confirmLabel: 'Withdraw', destructive: true }))) return;
    run('withdraw', 'Could not withdraw this order', () => onNavigate('orders'));
  };
  const off = { cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 };
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eef1f5' }}>
      <div style={{ ...MUTED, fontSize: 13, marginBottom: 10 }}>We haven&apos;t started on this order yet, so you can still change or withdraw it.</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={edit} disabled={busy} title="Take this order back into your cart to change it" style={{ ...ACTION_BUTTON, ...off }}>Edit order</button>
        <button type="button" onClick={withdraw} disabled={busy} title="Remove this order" style={{ ...ACTION_BUTTON, color: '#b91c1c', borderColor: '#fecaca', ...off }}>Withdraw order</button>
      </div>
      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function OrderDetail({ portalName, orderId, onBack, onNavigate, onCartChanged, onSessionEnded }) {
  const { data: order, error, notFound } = usePortalGet(portalName, `/orders/${encodeURIComponent(orderId)}`, null, onSessionEnded, 'Could not load this order');
  const back = <button type="button" onClick={onBack} title="Show all your orders" style={LINK_BUTTON}>‹ All orders</button>;

  if (notFound) {
    return (
      <div style={CARD_STYLE}>
        <div style={{ marginBottom: 12 }}>{back}</div>
        <div style={MUTED}>This order isn&apos;t available to you.</div>
      </div>
    );
  }
  if (error) return <div style={CARD_STYLE}><div style={{ marginBottom: 12 }}>{back}</div><div style={{ color: 'red', fontSize: 13 }}>{error}</div></div>;
  if (!order) return <div style={CARD_STYLE}><div style={MUTED}>Loading...</div></div>;

  const { totals } = order;
  // Shipped, Back-ordered and Cancelled show only when some line has one.
  const showShipped = order.lines.some((l) => l.shipped);
  const showBackordered = order.lines.some((l) => l.backordered);
  const showCancelled = order.lines.some((l) => l.cancelled);
  return (
    <>
      <div style={CARD_STYLE}>
        <div style={{ marginBottom: 12 }}>{back}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{order.display_number}</h1>
        </div>
        <OrderProgress status={order.status} label={order.status_label} />
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <Field label="Order date">{order.order_date}</Field>
          <Field label="PO reference">{order.po_reference || '—'}</Field>
          <Field label="Delivery address">
            {order.delivery_address.length ? order.delivery_address.map((part) => <div key={part}>{part}</div>) : '—'}
          </Field>
          {order.note && <Field label="Your note to us"><div style={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>{order.note}</div></Field>}
          {order.comment && <Field label="Our comment"><div style={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>{order.comment}</div></Field>}
        </div>
        {order.can_change && <OrderChanges portalName={portalName} order={order} onNavigate={onNavigate} onCartChanged={onCartChanged} onSessionEnded={onSessionEnded} />}
      </div>

      <div style={{ ...CARD_STYLE, marginTop: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Items</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                <th style={TH}>Product code</th>
                <th style={TH}>Description</th>
                <th style={TH}>Unit</th>
                <th style={{ ...TH, ...NUM }}>Ordered</th>
                <th style={{ ...TH, ...NUM }}>Unit price{order.price_suffix && <><br />{order.price_suffix.trim()}</>}</th>
                <th style={{ ...TH, ...NUM }}>Line total{order.price_suffix && <><br />{order.price_suffix.trim()}</>}</th>
                {showShipped && <th style={{ ...TH, ...NUM }}>Shipped</th>}
                {showBackordered && <th style={{ ...TH, ...NUM }}>Back-<br />ordered</th>}
                {showCancelled && <th style={{ ...TH, ...NUM }}>Cancelled</th>}
              </tr>
            </thead>
            <tbody>
              {order.lines.map((line) => (
                <tr key={line.id} style={{ borderBottom: '1px solid #eef1f5' }}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{line.product_code}</td>
                  <td style={TD}>{line.description}</td>
                  <td style={TD}>{line.unit}</td>
                  <td style={{ ...TD, ...NUM }}>{line.ordered}</td>
                  <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>
                    {line.unit_price || '—'}
                  </td>
                  <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>
                    {line.line_total}
                  </td>
                  {showShipped && <td style={{ ...TD, ...NUM }}>{line.shipped}</td>}
                  {showBackordered && <td style={{ ...TD, ...NUM, color: line.has_backorder ? '#b91c1c' : 'inherit', fontWeight: line.has_backorder ? 600 : 400 }}>{line.backordered}</td>}
                  {showCancelled && <td style={{ ...TD, ...NUM }}>{line.cancelled}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <div style={{ width: '100%', maxWidth: 340 }}>
            {/* Business: Subtotal (ex GST), GST, Total (inc GST). Retail: Total
                (inc GST), then the GST it includes. */}
            {totals.subtotal && <TotalRow label={totals.subtotal_label} value={totals.subtotal} />}
            {totals.discount && <TotalRow label={totals.discount_label} value={totals.discount} />}
            <TotalRow label={totals.shipping_label} value={totals.shipping ?? 'To be added'} />
            {totals.options && <TotalRow label={totals.options_label} value={totals.options} />}
            {totals.gst && order.pricing_basis !== 'retail' && <TotalRow label={totals.gst_label} value={totals.gst} />}
            <TotalRow label={totals.total_label} value={totals.total} strong />
            {totals.gst && order.pricing_basis === 'retail' && <TotalRow label={totals.gst_label} value={totals.gst} />}
          </div>
        </div>
      </div>

      <div style={{ ...CARD_STYLE, marginTop: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Shipments</h2>
        {order.shipments.length === 0 ? (
          <div style={MUTED}>Nothing has shipped yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                  <th style={TH}>Ship date</th>
                  <th style={TH}>Carrier</th>
                  <th style={TH}>Tracking number</th>
                </tr>
              </thead>
              <tbody>
                {order.shipments.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #eef1f5' }}>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{s.shipped_date || '—'}</td>
                    <td style={TD}>{s.carrier || '—'}</td>
                    <td style={{ ...TD, wordBreak: 'break-all' }}>
                      {s.tracking_url ? (
                        <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" title={`Track this shipment on ${s.carrier}'s website`} style={{ color: '#1976d2' }}>
                          {s.tracking_number}
                        </a>
                      ) : (s.tracking_number || '—')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// `orderId` set shows that order; otherwise the list.
function PortalOrdersPage({ portalName, orderId, ordering = { can_order: false, message: '' }, onNavigate, onCartChanged, onSessionEnded }) {
  if (orderId) {
    return <OrderDetail portalName={portalName} orderId={orderId} onBack={() => onNavigate('orders')} onNavigate={onNavigate} onCartChanged={onCartChanged} onSessionEnded={onSessionEnded} />;
  }
  return <OrderList portalName={portalName} ordering={ordering} onOpen={(id) => onNavigate(`orders/${id}`)} onNew={() => onNavigate('order')} onSessionEnded={onSessionEnded} />;
}

export default PortalOrdersPage;
